/**
 * Residual Settings Page (Super Admin)
 * Fee template management (Schedule A) and master cost rate configuration.
 * References the Settings.html mockup.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Settings, Plus, Edit, Trash2, Save, FileText, DollarSign, Layers,
  Upload, Database, AlertCircle, CheckCircle2, RefreshCw, Wallet, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { displayProcessor, uniqueProcessors } from '../../lib/processorDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PROCESSORS = uniqueProcessors(['TSYS-msc', 'North', 'FiservCopilot', 'Fiserv', 'TSYS', 'Elavon', 'EPX', 'First Data']);

const DEFAULT_LINE_ITEMS = [
  { category: 'Discount Rates', fee_name: 'Qualified Rate', rate_type: 'percentage', rate_value: 0 },
  { category: 'Discount Rates', fee_name: 'Mid-Qualified Rate', rate_type: 'percentage', rate_value: 0 },
  { category: 'Discount Rates', fee_name: 'Non-Qualified Rate', rate_type: 'percentage', rate_value: 0 },
  { category: 'Transaction Fees', fee_name: 'Auth Fee', rate_type: 'fixed', rate_value: 0 },
  { category: 'Transaction Fees', fee_name: 'Batch Fee', rate_type: 'fixed', rate_value: 0 },
  { category: 'Transaction Fees', fee_name: 'AVS Fee', rate_type: 'fixed', rate_value: 0 },
  { category: 'Monthly Fees', fee_name: 'Statement Fee', rate_type: 'fixed', rate_value: 0 },
  { category: 'Monthly Fees', fee_name: 'PCI Compliance', rate_type: 'fixed', rate_value: 0 },
  { category: 'Monthly Fees', fee_name: 'Gateway Fee', rate_type: 'fixed', rate_value: 0 },
  { category: 'Annual Fees', fee_name: 'Annual Fee', rate_type: 'fixed', rate_value: 0 },
  { category: 'Annual Fees', fee_name: 'Regulatory Fee', rate_type: 'fixed', rate_value: 0 },
];

export default function ResidualSettings() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterProcessor, setFilterProcessor] = useState('');
  const [activeTab, setActiveTab] = useState('templates'); // templates | import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Import form state
  const [importProcessor, setImportProcessor] = useState('TSYS-msc');
  const [importPeriod, setImportPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [importMerchants, setImportMerchants] = useState([
    { name: '', mid: '', gross: '', costs: '' }
  ]);

  // Live Processor Sync tab state
  const [tenants, setTenants] = useState([]);
  const [syncTenantId, setSyncTenantId] = useState('');
  const [costSchedule, setCostSchedule] = useState({ buy_rate_multiplier: 1, markup_flat_fee: 0, markup_pct_fee: 0, notes: '' });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncPeriod, setSyncPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [processorData, setProcessorData] = useState([]);
  const [agentCommissions, setAgentCommissions] = useState([]);
  const [payoutLedger, setPayoutLedger] = useState([]);
  const [ledgerBusy, setLedgerBusy] = useState(null);
  const [loadingSyncTab, setLoadingSyncTab] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '',
    processor: '',
    description: '',
    tenant_id: null,
    line_items: [...DEFAULT_LINE_ITEMS],
  });

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterProcessor ? `?processor=${filterProcessor}` : '';
      const data = await api.request(`/residuals/fee-templates${params}`);
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
    setLoading(false);
  }, [filterProcessor]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ── Live Processor Sync ──
  const loadTenants = useCallback(async () => {
    try {
      const data = await api.request('/super-admin/tenants');
      setTenants(data);
      setSyncTenantId(prev => prev || (data.length > 0 ? data[0].id : ''));
    } catch (err) {
      console.error('Failed to load tenants:', err);
    }
  }, []);

  const loadCostSchedule = useCallback(async (tenantId) => {
    if (!tenantId) return;
    try {
      const data = await api.request(`/residuals/cost-schedule?tenant_id=${tenantId}`);
      const existing = data.find(s => s.processor_slug === 'maverick');
      setCostSchedule(existing
        ? { buy_rate_multiplier: existing.buy_rate_multiplier, markup_flat_fee: existing.markup_flat_fee, markup_pct_fee: existing.markup_pct_fee, notes: existing.notes || '' }
        : { buy_rate_multiplier: 1, markup_flat_fee: 0, markup_pct_fee: 0, notes: '' });
    } catch (err) {
      console.error('Failed to load cost schedule:', err);
    }
  }, []);

  const loadSyncTabData = useCallback(async (tenantId, period) => {
    if (!tenantId) return;
    setLoadingSyncTab(true);
    try {
      const [pdata, acomm, ledger] = await Promise.all([
        api.request(`/residuals/processor-data?tenant_id=${tenantId}&period=${period}`),
        api.request(`/residuals/agent-commissions?period=${period}&tenant_id=${tenantId}`),
        api.request(`/residuals/payout-ledger?period=${period}&tenant_id=${tenantId}`),
      ]);
      setProcessorData(pdata);
      setAgentCommissions(acomm);
      setPayoutLedger(ledger);
    } catch (err) {
      console.error('Failed to load sync tab data:', err);
    }
    setLoadingSyncTab(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'sync') {
      loadTenants();
    }
  }, [activeTab, loadTenants]);

  useEffect(() => {
    if (activeTab === 'sync' && syncTenantId) {
      loadCostSchedule(syncTenantId);
      loadSyncTabData(syncTenantId, syncPeriod);
    }
  }, [activeTab, syncTenantId, syncPeriod, loadCostSchedule, loadSyncTabData]);

  const handleSaveCostSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.request('/residuals/cost-schedule', {
        method: 'POST',
        body: JSON.stringify({
          tenant_id: syncTenantId,
          processor: 'maverick',
          buy_rate_multiplier: parseFloat(costSchedule.buy_rate_multiplier) || 1,
          markup_flat_fee: parseFloat(costSchedule.markup_flat_fee) || 0,
          markup_pct_fee: parseFloat(costSchedule.markup_pct_fee) || 0,
          notes: costSchedule.notes,
        }),
      });
      toast.success('MSC Cost Schedule saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save cost schedule');
    }
    setSavingSchedule(false);
  };

  const handleProcessorSync = async () => {
    if (!syncTenantId) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.request('/residuals/processor-sync', {
        method: 'POST',
        body: JSON.stringify({ processor: 'maverick', tenant_id: syncTenantId }),
      });
      setSyncResult(res);
      toast.success(`Synced ${res.sync.synced} residual records, calculated ${res.calculation.calculated} commission entries`);
      loadSyncTabData(syncTenantId, syncPeriod);
    } catch (err) {
      toast.error(err.message || 'Sync failed');
    }
    setSyncing(false);
  };

  const handleGenerateLedger = async () => {
    try {
      const res = await api.request('/residuals/payout-ledger/generate', {
        method: 'POST',
        body: JSON.stringify({ period: syncPeriod, processor: 'maverick', tenant_id: syncTenantId }),
      });
      toast.success(`Generated ${res.created} new, updated ${res.updated}, skipped ${res.skipped_in_progress} already in progress`);
      loadSyncTabData(syncTenantId, syncPeriod);
    } catch (err) {
      toast.error(err.message || 'Failed to generate ledger');
    }
  };

  const LEDGER_ACTION_LABELS = {
    'pending-approval': 'Pending Approval', approve: 'Approved', request: 'Payout Requested',
    submit: 'Submitted', 'mark-paid': 'Paid', hold: 'Held', cancel: 'Cancelled', return: 'Returned', adjust: 'Adjusted',
  };

  const handleLedgerAction = async (ledgerId, action, extra = {}) => {
    setLedgerBusy(ledgerId);
    try {
      await api.request(`/residuals/payout-ledger/${ledgerId}/${action}`, {
        method: 'POST',
        body: JSON.stringify(extra),
      });
      toast.success(`Marked as ${LEDGER_ACTION_LABELS[action] || action}`);
      loadSyncTabData(syncTenantId, syncPeriod);
    } catch (err) {
      toast.error(err.message || 'Action failed');
    }
    setLedgerBusy(null);
  };

  const NEXT_ACTION = {
    calculated: { label: 'Send for Approval', action: 'pending-approval' },
    pending_approval: { label: 'Approve', action: 'approve' },
    approved: { label: 'Request Payout', action: 'request' },
    payout_requested: { label: 'Submit', action: 'submit' },
    submitted: { label: 'Mark Paid', action: 'mark-paid' },
    settled: { label: 'Mark Paid', action: 'mark-paid' },
    held: { label: 'Re-approve', action: 'approve' },
    failed: { label: 'Retry', action: 'request' },
    returned: { label: 'Retry', action: 'request' },
    adjusted: { label: 'Send for Approval', action: 'pending-approval' },
  };
  const HOLDABLE_STATUSES = ['calculated', 'pending_approval', 'approved', 'payout_requested', 'submitted', 'failed', 'returned'];

  const handleSave = async () => {
    try {
      if (editing) {
        await api.request(`/residuals/fee-templates/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: form.name,
            processor: form.processor,
            description: form.description,
            line_items: form.line_items,
          }),
        });
      } else {
        await api.request('/residuals/fee-templates', {
          method: 'POST',
          body: JSON.stringify(form),
        });
      }
      setShowDialog(false);
      resetForm();
      loadTemplates();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.request(`/residuals/fee-templates/${id}`, { method: 'DELETE' });
      loadTemplates();
    } catch (err) {
      alert(err.message);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      processor: '',
      description: '',
      tenant_id: null,
      line_items: [...DEFAULT_LINE_ITEMS],
    });
    setEditing(null);
  };

  const openEdit = (template) => {
    setEditing(template);
    setForm({
      name: template.name,
      processor: template.processor,
      description: template.description || '',
      tenant_id: template.tenant_id,
      line_items: template.line_items?.length > 0 ? template.line_items : [...DEFAULT_LINE_ITEMS],
    });
    setShowDialog(true);
  };

  const openCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const updateLineItem = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) =>
        i === idx ? { ...item, [field]: field === 'rate_value' ? parseFloat(value) || 0 : value } : item
      ),
    }));
  };

  const addLineItem = () => {
    setForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { category: '', fee_name: '', rate_type: 'fixed', rate_value: 0 }],
    }));
  };

  const removeLineItem = (idx) => {
    setForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== idx),
    }));
  };

  // ── Import Handlers ──
  const addImportMerchant = () => {
    setImportMerchants(prev => [...prev, { name: '', mid: '', gross: '', costs: '' }]);
  };

  const updateImportMerchant = (idx, field, value) => {
    setImportMerchants(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  const removeImportMerchant = (idx) => {
    setImportMerchants(prev => prev.filter((_, i) => i !== idx));
  };

  const handleProcessorImport = async () => {
    const validMerchants = importMerchants.filter(m => m.name && m.gross);
    if (validMerchants.length === 0) {
      toast.error('Add at least one merchant with name and gross revenue');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const res = await api.request('/residuals/simulate-processor-import', {
        method: 'POST',
        body: JSON.stringify({
          processor: importProcessor,
          period: importPeriod,
          merchants: validMerchants.map(m => ({
            name: m.name,
            mid: m.mid,
            gross: parseFloat(m.gross) || 0,
            costs: parseFloat(m.costs) || 0,
          })),
        }),
      });
      setImportResult(res);
      toast.success(`Imported ${res.imported_reports} reports, calculated ${res.calculated_payouts} payouts`);
    } catch (err) {
      toast.error(err.message || 'Import failed');
    }
    setImporting(false);
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/residuals/import-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('CSV import failed');
      const data = await res.json();
      toast.success(`Imported ${data.imported_count} records from CSV`);
      setImportResult({ imported_reports: data.imported_count, calculated_payouts: 0 });
    } catch (err) {
      toast.error(err.message || 'CSV import failed');
    }
    setImporting(false);
    e.target.value = '';
  };

  // Group line items by category for display
  const groupByCategory = (items) => {
    const groups = {};
    items.forEach((item, idx) => {
      const cat = item.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push({ ...item, _idx: idx });
    });
    return groups;
  };

  return (
    <div className="space-y-6" data-testid="residual-settings">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-heading" data-testid="settings-page-title">
            Residuals Management
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Fee templates, processor data imports, and Schedule A management
          </p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'templates' && (
            <Button onClick={openCreate} data-testid="create-template-btn">
              <Plus className="h-4 w-4 mr-2" /> New Template
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b overflow-x-auto">
        {[
          { key: 'templates', label: 'Fee Templates', icon: FileText },
          { key: 'import', label: 'Processor Import', icon: Database },
          { key: 'sync', label: 'Live Processor Sync', icon: RefreshCw },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-2 px-4 sm:px-5 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* ═══ TEMPLATES TAB ═══ */}
      {activeTab === 'templates' && (
        <>
          {/* Filter */}
          <div className="flex flex-wrap items-center gap-3">
            <Select value={filterProcessor || "all"} onValueChange={v => setFilterProcessor(v === "all" ? "" : v)}>
              <SelectTrigger className="w-44 sm:w-48" data-testid="filter-processor">
                <SelectValue placeholder="All Processors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Processors</SelectItem>
                {PROCESSORS.map(p => (
                  <SelectItem key={p} value={p}>{displayProcessor(p)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading templates...</div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 bg-card border rounded-xl">
          <Layers className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">No fee templates yet.</p>
          <p className="text-sm text-muted-foreground">Create your first Schedule A template to define processor cost rates.</p>
          <Button onClick={openCreate} className="mt-4" variant="outline">
            <Plus className="h-4 w-4 mr-2" /> Create Template
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div key={t.id} className="bg-card border rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <FileText className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">{t.name}</h3>
                    <p className="text-xs text-muted-foreground">{t.processor}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(t)}>
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(t.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {t.description && (
                <p className="text-xs text-muted-foreground mb-3">{t.description}</p>
              )}

              <div className="flex items-center gap-2 mb-3">
                {t.is_master && (
                  <span className="text-[10px] px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full font-medium">Master</span>
                )}
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  t.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>{t.status}</span>
              </div>

              {/* Line items summary */}
              <div className="text-xs text-muted-foreground space-y-1 border-t pt-2">
                {(t.line_items || []).slice(0, 4).map((li, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span>{li.fee_name}</span>
                    <span className="font-medium">
                      {li.rate_type === 'percentage' ? `${li.rate_value}%` : `$${li.rate_value}`}
                    </span>
                  </div>
                ))}
                {(t.line_items || []).length > 4 && (
                  <p className="text-muted-foreground/60 text-center">+{t.line_items.length - 4} more</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {/* ═══ IMPORT TAB ═══ */}
      {activeTab === 'import' && (
        <div className="space-y-6">
          {/* Import Methods */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Manual Processor Import */}
            <div className="bg-card border rounded-xl p-5 sm:p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Database className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Processor Data Import</h3>
                  <p className="text-xs text-muted-foreground">Enter merchant data from TSYS, Fiserv, Global/EPX, Cygma or Square</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block">Processor</label>
                  <Select value={importProcessor} onValueChange={setImportProcessor}>
                    <SelectTrigger className="h-9" data-testid="import-processor-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROCESSORS.map(p => <SelectItem key={p} value={p}>{displayProcessor(p)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block">Period (Month)</label>
                  <Input
                    type="month"
                    value={importPeriod}
                    onChange={e => setImportPeriod(e.target.value)}
                    className="h-9"
                    data-testid="import-period-input"
                  />
                </div>
              </div>

              {/* Merchants */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold">Merchants</label>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addImportMerchant} data-testid="add-import-merchant-btn">
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                </div>
                {importMerchants.map((m, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_80px_80px_80px_28px] gap-2 items-center" data-testid={`import-merchant-${idx}`}>
                    <Input value={m.name} onChange={e => updateImportMerchant(idx, 'name', e.target.value)} placeholder="Name" className="h-8 text-xs" />
                    <Input value={m.mid} onChange={e => updateImportMerchant(idx, 'mid', e.target.value)} placeholder="MID" className="h-8 text-xs" />
                    <Input type="number" value={m.gross} onChange={e => updateImportMerchant(idx, 'gross', e.target.value)} placeholder="Gross" className="h-8 text-xs" />
                    <Input type="number" value={m.costs} onChange={e => updateImportMerchant(idx, 'costs', e.target.value)} placeholder="Cost" className="h-8 text-xs" />
                    {importMerchants.length > 1 && (
                      <button onClick={() => removeImportMerchant(idx)} className="h-7 w-7 flex items-center justify-center text-red-400 hover:text-red-600 rounded hover:bg-red-50">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <Button onClick={handleProcessorImport} disabled={importing} className="w-full" data-testid="run-import-btn">
                {importing ? (
                  <><span className="animate-spin mr-2">&#9696;</span> Importing...</>
                ) : (
                  <><Database className="h-4 w-4 mr-2" /> Import & Calculate Residuals</>
                )}
              </Button>
            </div>

            {/* CSV Upload */}
            <div className="bg-card border rounded-xl p-5 sm:p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Upload className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold">CSV Upload</h3>
                  <p className="text-xs text-muted-foreground">Upload residual data from a CSV file</p>
                </div>
              </div>

              <div className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-primary/40 transition-colors"
                onClick={() => document.getElementById('csv-upload-input').click()}
                data-testid="csv-upload-zone"
              >
                <input
                  id="csv-upload-input"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleCsvUpload}
                />
                <Upload className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm font-medium">Click to upload CSV</p>
                <p className="text-xs text-muted-foreground mt-1">Required columns: period, processor, gross_revenue, processing_costs, net_revenue</p>
              </div>

              {/* CSV Template */}
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs font-semibold mb-2">Expected CSV Format:</p>
                <pre className="text-[10px] text-muted-foreground font-mono overflow-x-auto">
{`period,processor,gross_revenue,processing_costs,net_revenue,merchant_name
2026-03,TSYS-msc,5000.00,1000.00,4000.00,ABC Restaurant
2026-03,North,3000.00,600.00,2400.00,XYZ Retail`}
                </pre>
              </div>
            </div>
          </div>

          {/* Import Results */}
          {importResult && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 space-y-3" data-testid="import-result">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-semibold text-green-800">Import Successful</p>
                  <p className="text-xs text-green-600">
                    {importResult.imported_reports} report(s) imported
                    {importResult.calculated_payouts > 0 && `, ${importResult.calculated_payouts} payout(s) calculated`}
                  </p>
                </div>
              </div>
              {importResult.payouts_summary && importResult.payouts_summary.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-green-800">Cascading Payout Summary:</p>
                  {importResult.payouts_summary.map((p, idx) => (
                    <div key={idx} className="flex justify-between text-xs text-green-700 bg-green-100/50 rounded px-3 py-1.5">
                      <span>User {p.user_id?.slice(-6)} — {p.split_pct}% split</span>
                      <span className="font-bold">${p.payout?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ LIVE PROCESSOR SYNC TAB ═══ */}
      {activeTab === 'sync' && (
        <div className="space-y-6" data-testid="sync-tab-content">
          <p className="text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
            Four separate layers, on purpose: <strong>Processor Data</strong> (what happened) →{' '}
            <strong>MSC Cost Schedule</strong> (what MSC earned) → <strong>Agent Schedule A</strong> (what the
            agent is entitled to, above) → <strong>Payout Ledger</strong> (what was actually paid). USIO ACH
            submission is not wired yet — "Submit" below is an admin-confirmed manual step.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Select value={syncTenantId} onValueChange={setSyncTenantId}>
              <SelectTrigger className="w-56" data-testid="sync-tenant-select">
                <SelectValue placeholder="Select ISO tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenants.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="month" value={syncPeriod} onChange={e => setSyncPeriod(e.target.value)}
              className="w-40 h-9" data-testid="sync-period-input"
            />
            <Button onClick={handleProcessorSync} disabled={syncing || !syncTenantId} data-testid="sync-now-btn">
              {syncing ? (<><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Syncing...</>) : (<><RefreshCw className="h-4 w-4 mr-2" /> Sync Now</>)}
            </Button>
          </div>

          {syncResult && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800" data-testid="sync-result">
              Synced {syncResult.sync?.synced} residual record(s) from TSYS — recalculated {syncResult.calculation?.calculated} commission entries across {syncResult.calculation?.periods?.length || 0} period(s).
            </div>
          )}

          {/* MSC Cost Schedule */}
          <div className="bg-card border rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">MSC Cost Schedule</h3>
                <p className="text-xs text-muted-foreground">What MSC earns from this tenant, independent of what the processor reports</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium mb-1 block">Buy Rate Multiplier</label>
                <Input type="number" step="0.01" value={costSchedule.buy_rate_multiplier}
                  onChange={e => setCostSchedule(p => ({ ...p, buy_rate_multiplier: e.target.value }))}
                  data-testid="sync-buy-rate-input" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">Flat Markup Fee ($)</label>
                <Input type="number" step="0.01" value={costSchedule.markup_flat_fee}
                  onChange={e => setCostSchedule(p => ({ ...p, markup_flat_fee: e.target.value }))}
                  data-testid="sync-flat-fee-input" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">Markup Fee (%)</label>
                <Input type="number" step="0.01" value={costSchedule.markup_pct_fee}
                  onChange={e => setCostSchedule(p => ({ ...p, markup_pct_fee: e.target.value }))}
                  data-testid="sync-pct-fee-input" />
              </div>
            </div>
            <Input value={costSchedule.notes} onChange={e => setCostSchedule(p => ({ ...p, notes: e.target.value }))}
              placeholder="Notes..." data-testid="sync-notes-input" />
            <Button size="sm" onClick={handleSaveCostSchedule} disabled={savingSchedule} data-testid="save-sync-cost-schedule-btn">
              <Save className="h-3.5 w-3.5 mr-2" /> {savingSchedule ? 'Saving...' : 'Save Cost Schedule'}
            </Button>
          </div>

          {loadingSyncTab ? (
            <div className="text-center py-8 text-muted-foreground text-sm">Loading...</div>
          ) : (
            <>
              {/* Raw Processor Data */}
              <div className="bg-card border rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b font-semibold text-sm">Processor Data — {processorData.length} record(s) for {syncPeriod}</div>
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 sticky top-0"><tr>
                      <th className="text-left px-4 py-2">MID</th><th className="text-left px-4 py-2">Date</th>
                      <th className="text-right px-4 py-2">Processor Net Revenue</th><th className="text-right px-4 py-2">Rep Profit (raw)</th>
                    </tr></thead>
                    <tbody>
                      {processorData.map(r => (
                        <tr key={r.id} className="border-t" data-testid={`processor-data-row-${r.id}`}>
                          <td className="px-4 py-2">{r.mid}</td><td className="px-4 py-2">{r.date}</td>
                          <td className="px-4 py-2 text-right">${r.processor_net_revenue?.toFixed(2)}</td>
                          <td className="px-4 py-2 text-right">${r.processor_user_profit?.toFixed(2)}</td>
                        </tr>
                      ))}
                      {processorData.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">No processor data synced for this period yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Agent Commissions */}
              <div className="bg-card border rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b font-semibold text-sm">Agent Commissions (Schedule A applied) — {syncPeriod}</div>
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 sticky top-0"><tr>
                      <th className="text-left px-4 py-2">Agent</th><th className="text-left px-4 py-2">MID</th>
                      <th className="text-right px-4 py-2">Split %</th><th className="text-right px-4 py-2">MSC Net</th>
                      <th className="text-right px-4 py-2">Entitled</th>
                    </tr></thead>
                    <tbody>
                      {agentCommissions.map(c => (
                        <tr key={c.id} className="border-t" data-testid={`agent-commission-row-${c.id}`}>
                          <td className="px-4 py-2">{c.user_name || c.user_id}</td><td className="px-4 py-2">{c.mid}</td>
                          <td className="px-4 py-2 text-right">{c.split_percentage}%</td>
                          <td className={`px-4 py-2 text-right ${c.msc_net_revenue < 0 ? 'text-red-600' : ''}`}>${c.msc_net_revenue?.toFixed(2)}</td>
                          <td className={`px-4 py-2 text-right font-semibold ${c.entitled_amount < 0 ? 'text-red-600' : ''}`}>${c.entitled_amount?.toFixed(2)}</td>
                        </tr>
                      ))}
                      {agentCommissions.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No commissions calculated for this period yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Payout Ledger */}
              <div className="bg-card border rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b flex items-center justify-between">
                  <div className="font-semibold text-sm flex items-center gap-2"><Wallet className="h-4 w-4" /> Payout Ledger — {syncPeriod}</div>
                  <Button size="sm" variant="outline" onClick={handleGenerateLedger} data-testid="generate-ledger-btn">
                    Generate from Commissions
                  </Button>
                </div>
                <div className="overflow-x-auto max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 sticky top-0"><tr>
                      <th className="text-left px-4 py-2">Agent</th><th className="text-right px-4 py-2">Amount</th>
                      <th className="text-left px-4 py-2">Status</th><th className="text-right px-4 py-2">Action</th>
                    </tr></thead>
                    <tbody>
                      {payoutLedger.map(l => {
                        const next = NEXT_ACTION[l.status];
                        const blockedByNegative = l.amount < 0 && next?.action === 'mark-paid';
                        return (
                          <tr key={l.id} className="border-t" data-testid={`payout-ledger-row-${l.id}`}>
                            <td className="px-4 py-2">{l.user_name || l.user_id}</td>
                            <td className={`px-4 py-2 text-right font-semibold ${l.amount < 0 ? 'text-red-600' : ''}`}>${l.amount?.toFixed(2)}</td>
                            <td className="px-4 py-2">
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                l.status === 'paid' ? 'bg-green-50 text-green-700' :
                                ['failed', 'returned', 'cancelled'].includes(l.status) ? 'bg-red-50 text-red-700' :
                                l.status === 'held' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
                              }`}>{l.status.replace(/_/g, ' ')}</span>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex gap-1 justify-end">
                                {next && !blockedByNegative && (
                                  <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                    disabled={ledgerBusy === l.id}
                                    onClick={() => handleLedgerAction(l.id, next.action)}
                                    data-testid={`ledger-action-${l.id}`}
                                  >{next.label} <ArrowRight className="h-3 w-3 ml-1" /></Button>
                                )}
                                {blockedByNegative && (
                                  <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600 border-red-200"
                                    disabled={ledgerBusy === l.id}
                                    onClick={() => handleLedgerAction(l.id, 'adjust', { adjusted_amount: 0, note: 'Zeroed out — negative amount' })}
                                    data-testid={`ledger-adjust-${l.id}`}
                                  >Adjust First</Button>
                                )}
                                {HOLDABLE_STATUSES.includes(l.status) && (
                                  <Button size="sm" variant="ghost" className="h-7 text-[11px] text-amber-600"
                                    disabled={ledgerBusy === l.id}
                                    onClick={() => handleLedgerAction(l.id, 'hold')}
                                    data-testid={`ledger-hold-${l.id}`}
                                  >Hold</Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {payoutLedger.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">No payout ledger entries for this period yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) { setShowDialog(false); resetForm(); } }}>
        <DialogContent className="max-w-[900px] w-[90vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="template-dialog-title">
              {editing ? 'Edit Fee Template' : 'Create Fee Template (Schedule A)'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Template Name</label>
                <Input
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., TSYS-msc Standard Cost"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Processor</label>
                <Select value={form.processor} onValueChange={v => setForm(p => ({ ...p, processor: v }))}>
                  <SelectTrigger data-testid="template-processor-select">
                    <SelectValue placeholder="Select processor" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROCESSORS.map(p => (
                      <SelectItem key={p} value={p}>{displayProcessor(p)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Input
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Optional description..."
                data-testid="template-description-input"
              />
            </div>

            {/* Line Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-primary" />
                  Fee Line Items
                </h3>
                <Button variant="outline" size="sm" onClick={addLineItem} data-testid="add-line-item-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
                </Button>
              </div>

              {Object.entries(groupByCategory(form.line_items)).map(([category, items]) => (
                <div key={category} className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">{category}</p>
                  {items.map((item) => (
                    <div key={item._idx} className="grid grid-cols-[1fr_1.5fr_100px_100px_36px] items-center gap-3 bg-muted/30 rounded-lg p-2.5">
                      <Input
                        value={item.category}
                        onChange={e => updateLineItem(item._idx, 'category', e.target.value)}
                        placeholder="Category"
                        className="h-9 text-sm"
                      />
                      <Input
                        value={item.fee_name}
                        onChange={e => updateLineItem(item._idx, 'fee_name', e.target.value)}
                        placeholder="Fee name"
                        className="h-9 text-sm"
                      />
                      <Select
                        value={item.rate_type}
                        onValueChange={v => updateLineItem(item._idx, 'rate_type', v)}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">%</SelectItem>
                          <SelectItem value="fixed">$</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          {item.rate_type === 'percentage' ? '%' : '$'}
                        </span>
                        <Input
                          type="number"
                          value={item.rate_value}
                          onChange={e => updateLineItem(item._idx, 'rate_value', e.target.value)}
                          className="pl-7 h-9 text-sm"
                          step="0.01"
                        />
                      </div>
                      <Button variant="ghost" size="sm" className="h-9 w-9 p-0 text-red-400" onClick={() => removeLineItem(item._idx)}>
                        &times;
                      </Button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={!form.name || !form.processor} data-testid="save-template-btn">
              <Save className="h-4 w-4 mr-2" />
              {editing ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
